import { useState, useEffect } from 'react';
import { Search, Bell, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AuditRun, AIResponse } from '../../types';
import { Tooltip } from '../shared';
import { AI_SYSTEMS } from '../../config/ai';

/**
 * Visibility Score Screen - Track your score over time
 */
export function VisibilityScore() {
  const { user } = useAuth();
  const [latestAudit, setLatestAudit] = useState<AuditRun | null>(null);
  const [historicalAudits, setHistoricalAudits] = useState<AuditRun[]>([]);
  const [platformScores, setPlatformScores] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      // Load latest audit
      const latestQuery = query(
        collection(db, 'auditRuns'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc'),
        limit(1)
      );
      const latestSnapshot = await getDocs(latestQuery);
      if (!latestSnapshot.empty) {
        const audit = latestSnapshot.docs[0].data() as AuditRun;
        setLatestAudit(audit);

        // Load platform-specific scores from AI responses
        const responsesQuery = query(
          collection(db, 'aiResponses'),
          where('auditRunId', '==', audit.id)
        );
        const responsesSnapshot = await getDocs(responsesQuery);
        const responses = responsesSnapshot.docs.map(doc => doc.data() as AIResponse);

        // Calculate average score per platform
        const scores: Record<string, number[]> = {};
        responses.forEach(response => {
          if (!scores[response.aiSystem]) scores[response.aiSystem] = [];
          scores[response.aiSystem].push(response.sentimentScore || 0);
        });

        const avgScores: Record<string, number> = {};
        Object.keys(scores).forEach(platform => {
          const avg = scores[platform].reduce((a, b) => a + b, 0) / scores[platform].length;
          avgScores[platform] = Math.round(avg);
        });
        setPlatformScores(avgScores);
      }

      // Load historical audits for trend
      const historicalQuery = query(
        collection(db, 'auditRuns'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc'),
        limit(30)
      );
      const historicalSnapshot = await getDocs(historicalQuery);
      setHistoricalAudits(
        historicalSnapshot.docs.map(doc => doc.data() as AuditRun).reverse()
      );
    } catch (error) {
      console.error('Error loading visibility score data:', error);
    } finally {
      setLoading(false);
    }
  };

  const score = latestAudit?.visibilityScore || 0;
  const previousScore = latestAudit?.previousScore || 0;
  const scoreDelta = previousScore ? score - previousScore : 0;

  const hasTrendData = historicalAudits.length >= 2;
  const trend = hasTrendData ? historicalAudits.map(a => a.visibilityScore || 0) : [score];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t1-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Visibility score</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t1-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
          <h1 className="text-3xl font-semibold text-gray-900">Visibility Score</h1>
          <p className="text-sm text-gray-600 mt-1">Track your brand visibility across AI platforms over time</p>
        </div>

        {/* Current Score Card */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <p className="text-sm text-gray-600 mb-2">Current Score</p>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold text-t1-primary">{score}</span>
              <span className="text-xl text-gray-400">/100</span>
            </div>
            {scoreDelta !== 0 && (
              <div className={`flex items-center gap-1 mt-2 ${scoreDelta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {scoreDelta > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span className="text-sm font-medium">{scoreDelta > 0 ? '+' : ''}{scoreDelta} from last scan</span>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <p className="text-sm text-gray-600 mb-2">30-Day Average</p>
            {hasTrendData && trend.length >= 5 ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-gray-900">
                    {Math.round(trend.slice(-5).reduce((a, b) => a + b, 0) / 5)}
                  </span>
                  <span className="text-xl text-gray-400">/100</span>
                </div>
              </>
            ) : (
              <Tooltip content="Run scans regularly over 30 days to see your average score">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-gray-400">—</span>
                </div>
              </Tooltip>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <p className="text-sm text-gray-600 mb-2">Best Score</p>
            {hasTrendData ? (
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-green-600">{Math.max(...trend)}</span>
                <span className="text-xl text-gray-400">/100</span>
              </div>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-green-600">{score}</span>
                <span className="text-xl text-gray-400">/100</span>
              </div>
            )}
          </div>
        </div>

        {/* Trend Chart */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Score History</h2>
          {hasTrendData ? (
            <>
              <div className="flex items-end justify-between gap-2 h-48 mb-4">
                {trend.map((value, index) => (
                  <div key={index} className="flex-1 flex flex-col justify-end">
                    <div
                      className={`rounded-t ${index === trend.length - 1 ? 'bg-t1-primary' : 'bg-gray-300'}`}
                      style={{ height: `${(value / 100) * 100}%`, minHeight: '4px' }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{trend.length} scans ago</span>
                <span>Now · {score}</span>
              </div>
            </>
          ) : (
            <Tooltip content="Run multiple scans to see your score trend over time">
              <div className="flex items-center justify-center h-48 text-gray-400">
                <div className="text-center">
                  <Minus className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">No historical data</p>
                </div>
              </div>
            </Tooltip>
          )}
        </div>

        {/* Platform Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Score by Platform</h2>
          {Object.keys(platformScores).length > 0 ? (
            <div className="space-y-4">
              {AI_SYSTEMS.filter(s => s.enabled).map(system => (
                <div key={system.id}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{system.name}</span>
                    <span className="text-sm font-semibold text-t1-primary">
                      {platformScores[system.id] || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-t1-primary h-2 rounded-full"
                      style={{ width: `${platformScores[system.id] || 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Tooltip content="Run an audit to see scores broken down by AI platform">
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No platform data available</p>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
